/**
 * @fileoverview FeedbackModal Component - User feedback collection modal
 * 
 * Provides a comprehensive feedback form with:
 * - Page rating on 1-5 scale
 * - Overall impression feedback
 * - Overall performance feedback  
 * - General comments section
 * - Submit to SharePoint PortalFeedback list
 * 
 * @component
 * @example
 * <FeedbackModal isOpen={true} onClose={() => {}} />
 * 
 * @requires React
 * @requires ../utils/apiHelper
 * @requires ./FeedbackModal.css
 * 
 * @author By John Bellew - Infinia.ie - last updated 22/07/2025
 */

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import apiHelper from '../utils/apiHelper';
import './FeedbackModal.css';

/**
 * FeedbackModal Component
 * 
 * Renders a comprehensive feedback collection modal with form validation and submission.
 * 
 * Features:
 * - Auto-detects current page for feedback context
 * - 1-5 star rating system
 * - Multi-line text areas for detailed feedback
 * - Email passed from parent component
 * - Form validation and error handling
 * - SharePoint integration for data storage
 * 
 * @component
 * @param {Object} props Component props
 * @param {boolean} props.isOpen Whether the modal is visible
 * @param {Function} props.onClose Callback function to close the modal
 * @param {string} props.userEmail User's email address from Auth0
 * @returns {JSX.Element} FeedbackModal component
 */
function FeedbackModal({ isOpen, onClose, userEmail }) {
  const location = useLocation();
  
  // Form state
  const [formData, setFormData] = useState({
    page: '',
    rating: '',
    overAllImpression: '',
    overAllPerformance: '',
    comments: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null
  const [errors, setErrors] = useState({});

  // Auto-populate page field when location changes
  useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({
        ...prev,
        page: getPageDisplayName(location.pathname)
      }));
    }
  }, [isOpen, location.pathname]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        page: '',
        rating: '',
        overAllImpression: '',
        overAllPerformance: '',
        comments: ''
      });
      setErrors({});
      setSubmitStatus(null);
    }
  }, [isOpen]);

  // Log modal open for activity tracking
  useEffect(() => {
    if (isOpen && userEmail) {
      apiHelper.logModalOpen(userEmail, 'FeedbackModal', 'Feedback', 'Feedback');
    }
  }, [isOpen, userEmail]);

  /**
   * Convert pathname to user-friendly page name
   * @param {string} pathname - The current URL pathname
   * @returns {string} User-friendly page name
   */
  const getPageDisplayName = (pathname) => {
    const pageMap = {
      '/': 'Home',
      '/newsroom': 'News Room',
      '/events': 'Events',
      '/workinggroups': 'Working Groups',
      '/policyregulatory': 'Policy & Regulatory',
      '/capabilityinnovation': 'Capability & Innovation',
      '/representation': 'Representation',
      '/etfbook': 'ETF Screener',
      '/mediacentre': 'Media Centre',
      '/councilcorner': 'Council Corner',
      '/profile': 'Profile',
      '/daire': 'Daire - AI Assistant',
      '/search': 'Search Results'
    };

    // Check for exact matches first
    if (pageMap[pathname]) {
      return pageMap[pathname];
    }

    // Handle dynamic routes
    if (pathname.startsWith('/newsroom/')) return 'News Article';
    if (pathname.startsWith('/events/')) return 'Event Details';
    if (pathname.startsWith('/workinggroups/')) return 'Working Group Details';
    if (pathname.startsWith('/policyregulatory/')) return 'Policy & Regulatory Page';
    if (pathname.startsWith('/capabilityinnovation/')) return 'Capability & Innovation Page';
    if (pathname.startsWith('/mediacentre/')) return 'Media Centre Content';

    // Default fallback
    return pathname.replace(/^\//, '').replace(/\//g, ' > ') || 'Home';
  };

  /**
   * Handle form input changes
   * @param {Event} e - Input change event
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  /**
   * Handle star rating selection
   * @param {number} rating - Selected rating (1-5)
   */
  const handleRatingSelect = (rating) => {
    setFormData(prev => ({
      ...prev,
      rating: rating.toString()
    }));
    
    // Clear rating error
    if (errors.rating) {
      setErrors(prev => ({
        ...prev,
        rating: ''
      }));
    }
  };

  /**
   * Validate form data
   * @returns {Object} Validation errors object
   */
  const validateForm = () => {
    const newErrors = {};

    if (!formData.rating) {
      newErrors.rating = 'Please select a rating';
    }

    if (!formData.overAllImpression.trim()) {
      newErrors.overAllImpression = 'Please provide your overall impression';
    }

    if (!formData.overAllPerformance.trim()) {
      newErrors.overAllPerformance = 'Please provide feedback on performance';
    }

    return newErrors;
  };

  /**
   * Handle form submission
   * @param {Event} e - Form submit event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    // Check if user email is available from props
    if (!userEmail) {
      setSubmitStatus('error');
      setErrors({ 
        email: 'Unable to identify user email. Please ensure you are properly logged in and try again.' 
      });
      return;
    }

    try {
      // Prepare data for submission
      const feedbackData = {
        email: userEmail, // Use the validated email
        page: formData.page,
        rating: formData.rating,
        overAllImpression: formData.overAllImpression,
        overAllPerformance: formData.overAllPerformance,
        comments: formData.comments
      };

      // Submit feedback
      await apiHelper.submitFeedback(feedbackData);
      
      setSubmitStatus('success');
      
      // Auto-close modal after 2 seconds on success
      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Render star rating component
   * @param {number} currentRating - Currently selected rating
   * @returns {JSX.Element} Star rating component
   */
  const renderStarRating = (currentRating) => {
    return (
      <div className="star-rating">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            className={`star ${currentRating >= star ? 'active' : ''}`}
            onClick={() => handleRatingSelect(star)}
            aria-label={`Rate ${star} star${star !== 1 ? 's' : ''}`}
          >
            ★
          </button>
        ))}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="feedback-modal-overlay" onClick={onClose}>
      <div className="feedback-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="feedback-modal-header">
          <h2>Share Your Feedback</h2>
          <button 
            className="feedback-close-button"
            onClick={onClose}
            aria-label="Close feedback modal"
          >
            ×
          </button>
        </div>

        <div className="feedback-modal-body">
          {submitStatus === 'success' && (
            <div className="feedback-success-message">
              <i className="fas fa-check-circle"></i>
              Thank you for your feedback! It has been submitted successfully.
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="feedback-error-message">
              <i className="fas fa-exclamation-triangle"></i>
              Sorry, there was an error submitting your feedback. Please try again.
            </div>
          )}

          {!userEmail && (
            <div className="feedback-error-message">
              <i className="fas fa-exclamation-triangle"></i>
              Unable to identify your email address. Please try logging out and back in.
            </div>
          )}

          {submitStatus !== 'success' && userEmail && (
            <form onSubmit={handleSubmit} className="feedback-form">
              <div className="feedback-form-row">
                <label htmlFor="page">Page</label>
                <input
                  type="text"
                  id="page"
                  name="page"
                  value={formData.page}
                  onChange={handleInputChange}
                  readOnly
                  className="feedback-page-input"
                />
              </div>

              <div className="feedback-form-row">
                <label>
                  Overall Rating <span className="required">*</span>
                </label>
                {renderStarRating(parseInt(formData.rating) || 0)}
                {errors.rating && (
                  <div className="feedback-error">{errors.rating}</div>
                )}
              </div>

              <div className="feedback-form-row">
                <label htmlFor="overAllImpression">
                  Overall Impression <span className="required">*</span>
                </label>
                <textarea
                  id="overAllImpression"
                  name="overAllImpression"
                  value={formData.overAllImpression}
                  onChange={handleInputChange}
                  placeholder="Please share your overall impression of this page..."
                  rows="3"
                  className={errors.overAllImpression ? 'error' : ''}
                />
                {errors.overAllImpression && (
                  <div className="feedback-error">{errors.overAllImpression}</div>
                )}
              </div>

              <div className="feedback-form-row">
                <label htmlFor="overAllPerformance">
                  Performance Feedback <span className="required">*</span>
                </label>
                <textarea
                  id="overAllPerformance"
                  name="overAllPerformance"
                  value={formData.overAllPerformance}
                  onChange={handleInputChange}
                  placeholder="How was the performance of this page? (loading speed, responsiveness, etc.)"
                  rows="3"
                  className={errors.overAllPerformance ? 'error' : ''}
                />
                {errors.overAllPerformance && (
                  <div className="feedback-error">{errors.overAllPerformance}</div>
                )}
              </div>

              <div className="feedback-form-row">
                <label htmlFor="comments">Additional Comments</label>
                <textarea
                  id="comments"
                  name="comments"
                  value={formData.comments}
                  onChange={handleInputChange}
                  placeholder="Any additional feedback or suggestions?"
                  rows="3"
                />
              </div>

              <div className="feedback-form-actions">
                <button
                  type="button"
                  onClick={onClose}
                  className="feedback-cancel-button"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="feedback-submit-button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane"></i>
                      Submit Feedback
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default FeedbackModal; 